// Sound effects, all made in code with the browser's Web Audio API: no audio files, nothing copied.
// Every sound is a recipe of oscillators (tones) and filtered noise with short volume envelopes.
// Browsers only allow sound after the player clicks or taps something, so the engine starts on
// the first click and is silent until then.
import { getSettings, onSettings } from './settings.ts';

let ac: AudioContext | null = null;
let masterGain: GainNode;
let sfxBus: GainNode;
let musicBus: GainNode;
let noiseBuf: AudioBuffer;
let unlocked = false;
const unlockListeners: (() => void)[] = [];

export function audioContext(): AudioContext | null {
  return ac;
}
export function musicDestination(): GainNode | null {
  return ac ? musicBus : null;
}
export function isUnlocked(): boolean {
  return unlocked;
}
/** Run `fn` once audio is allowed (immediately if it already is). */
export function whenUnlocked(fn: () => void) {
  if (unlocked) fn();
  else unlockListeners.push(fn);
}

function build() {
  const Ctor = (window.AudioContext ?? (window as any).webkitAudioContext) as typeof AudioContext | undefined;
  if (!Ctor) return;
  ac = new Ctor();
  // Phones: follow the ring/silent switch where the browser supports it.
  try {
    const session = (navigator as any).audioSession;
    if (session) session.type = 'ambient';
  } catch { /* not supported */ }
  const comp = ac.createDynamicsCompressor();
  comp.threshold.value = -14;
  comp.ratio.value = 4;
  masterGain = ac.createGain();
  sfxBus = ac.createGain();
  musicBus = ac.createGain();
  sfxBus.connect(masterGain);
  musicBus.connect(masterGain);
  masterGain.connect(comp);
  comp.connect(ac.destination);
  const len = ac.sampleRate * 2;
  noiseBuf = ac.createBuffer(1, len, ac.sampleRate);
  const d = noiseBuf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  applyVolumes();
}

function applyVolumes() {
  if (!ac) return;
  const s = getSettings();
  const t = ac.currentTime;
  masterGain.gain.setTargetAtTime(s.muted ? 0 : s.master, t, 0.05);
  sfxBus.gain.setTargetAtTime(s.sfx ? s.sfxVolume : 0, t, 0.05);
  musicBus.gain.setTargetAtTime(s.music ? s.musicVolume * 0.9 : 0, t, 0.2);
}
onSettings(applyVolumes);

/** Called on the first click/tap/key press anywhere on the page. */
export function unlockAudio() {
  if (!ac) build();
  if (!ac) return;
  if (ac.state === 'suspended') ac.resume().catch(() => {});
  if (!unlocked) {
    unlocked = true;
    for (const fn of unlockListeners.splice(0)) fn();
  }
}

export function installAudioUnlock() {
  const go = () => unlockAudio();
  for (const ev of ['pointerdown', 'keydown', 'touchend']) window.addEventListener(ev, go, { capture: true, passive: true });
  // Stay quiet in background tabs; pick up again when the player comes back.
  document.addEventListener('visibilitychange', () => {
    if (!ac) return;
    if (document.hidden) ac.suspend().catch(() => {});
    else if (unlocked) ac.resume().catch(() => {});
  });
}

// ---------------------------------------------------------------- building blocks

const midi = (n: number) => 440 * Math.pow(2, (n - 69) / 12);
const rand = (a: number, b: number) => a + Math.random() * (b - a);

interface Env { gain?: number; attack?: number; dur: number; curve?: 'exp' | 'lin' }

function env(g: GainNode, t: number, { gain = 0.3, attack = 0.005, dur, curve = 'exp' }: Env) {
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(gain, t + attack);
  if (curve === 'exp') g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  else g.gain.linearRampToValueAtTime(0.0001, t + dur);
}

interface ToneOpts extends Env {
  type?: OscillatorType;
  freq: number;
  to?: number;
  detune?: number;
  filter?: { type: BiquadFilterType; freq: number; q?: number; to?: number };
  vibrato?: { rate: number; depth: number };
}

function tone(out: AudioNode, t: number, o: ToneOpts) {
  const a = ac!;
  const osc = a.createOscillator();
  osc.type = o.type ?? 'sine';
  osc.frequency.setValueAtTime(o.freq, t);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(Math.max(1, o.to), t + o.dur);
  if (o.detune) osc.detune.value = o.detune;
  if (o.vibrato) {
    const lfo = a.createOscillator();
    const depth = a.createGain();
    lfo.frequency.value = o.vibrato.rate;
    depth.gain.value = o.vibrato.depth;
    lfo.connect(depth).connect(osc.frequency);
    lfo.start(t);
    lfo.stop(t + o.dur + 0.05);
  }
  const g = a.createGain();
  env(g, t, o);
  let node: AudioNode = osc;
  if (o.filter) {
    const f = a.createBiquadFilter();
    f.type = o.filter.type;
    f.frequency.setValueAtTime(o.filter.freq, t);
    if (o.filter.to) f.frequency.exponentialRampToValueAtTime(o.filter.to, t + o.dur);
    f.Q.value = o.filter.q ?? 1;
    node.connect(f);
    node = f;
  }
  node.connect(g).connect(out);
  osc.start(t);
  osc.stop(t + o.dur + 0.05);
}

interface NoiseOpts extends Env {
  type?: BiquadFilterType;
  freq?: number;
  to?: number;
  q?: number;
}

function noise(out: AudioNode, t: number, o: NoiseOpts) {
  const a = ac!;
  const src = a.createBufferSource();
  src.buffer = noiseBuf;
  src.loop = true;
  const f = a.createBiquadFilter();
  f.type = o.type ?? 'lowpass';
  f.frequency.setValueAtTime(o.freq ?? 2000, t);
  if (o.to) f.frequency.exponentialRampToValueAtTime(o.to, t + o.dur);
  f.Q.value = o.q ?? 0.8;
  const g = a.createGain();
  env(g, t, o);
  src.connect(f).connect(g).connect(out);
  src.start(t, Math.random() * 1.5);
  src.stop(t + o.dur + 0.05);
}

/** A bell: a few inharmonic partials that ring out. */
function bell(out: AudioNode, t: number, f: number, gain = 0.2, dur = 1.4) {
  const parts: [number, number, number][] = [[1, 1, 1], [2.76, 0.45, 0.6], [5.4, 0.22, 0.35], [8.93, 0.1, 0.2]];
  for (const [ratio, g, d] of parts) tone(out, t, { freq: f * ratio, gain: gain * g, dur: dur * d, attack: 0.002 });
}

/** A brassy note: two detuned saw waves through a lowpass that opens up. */
function brass(out: AudioNode, t: number, f: number, dur: number, gain = 0.12) {
  for (const det of [-7, 7]) {
    tone(out, t, { type: 'sawtooth', freq: f, detune: det, dur, gain, attack: 0.03, curve: 'lin', filter: { type: 'lowpass', freq: 900, to: 2600, q: 1.2 } });
  }
}

function coinPing(out: AudioNode, t: number, f = 1976, gain = 0.18) {
  tone(out, t, { type: 'triangle', freq: f, dur: 0.22, gain });
  tone(out, t + 0.05, { type: 'triangle', freq: f * 1.335, dur: 0.35, gain: gain * 0.9 });
  tone(out, t + 0.05, { freq: f * 2.67, dur: 0.2, gain: gain * 0.25 });
}

function punch(out: AudioNode, t: number, heavy = 1) {
  noise(out, t, { type: 'lowpass', freq: 1400 / heavy, to: 200, dur: 0.12 * heavy, gain: 0.6, attack: 0.002 });
  tone(out, t, { freq: 170 / heavy, to: 45, dur: 0.16 * heavy, gain: 0.7, attack: 0.002 });
}

function gunshot(out: AudioNode, t: number) {
  noise(out, t, { type: 'lowpass', freq: 6000, to: 500, dur: 0.32, gain: 0.85, attack: 0.001 });
  tone(out, t, { freq: 110, to: 38, dur: 0.22, gain: 0.6, attack: 0.001 });
  noise(out, t + 0.05, { type: 'bandpass', freq: 500, q: 0.7, dur: 0.7, gain: 0.12, attack: 0.02 }); // echo off the walls
}

function click(out: AudioNode, t: number, gain = 0.2, f = 3000) {
  noise(out, t, { type: 'bandpass', freq: f, q: 3, dur: 0.018, gain, attack: 0.001 });
}

function woodKnock(out: AudioNode, t: number) {
  tone(out, t, { freq: 210, to: 150, dur: 0.08, gain: 0.5, attack: 0.001 });
  noise(out, t, { type: 'bandpass', freq: 900, q: 2, dur: 0.05, gain: 0.35, attack: 0.001 });
}

function hoof(out: AudioNode, t: number, gain = 0.3) {
  noise(out, t, { type: 'lowpass', freq: 500, dur: 0.06, gain, attack: 0.002 });
  tone(out, t, { freq: 120, to: 70, dur: 0.07, gain: gain * 0.8, attack: 0.002 });
}

function drum(out: AudioNode, t: number, gain = 0.8) {
  tone(out, t, { freq: 80, to: 45, dur: 0.55, gain, attack: 0.003 });
  noise(out, t, { type: 'lowpass', freq: 300, dur: 0.18, gain: gain * 0.5, attack: 0.002 });
}

// ---------------------------------------------------------------- the sounds

type Recipe = (out: AudioNode, t: number, o: PlayOpts) => number; // returns length in seconds

export interface PlayOpts {
  /** For sounds that run as long as an animation (slot spin, roulette ball, hooves). */
  duration?: number;
  /** 0..1 scale on top of the volume settings. */
  volume?: number;
}

const RECIPES: Record<string, Recipe> = {
  click: (o, t) => { click(o, t, 0.12, 2600); tone(o, t, { freq: 1800, dur: 0.02, gain: 0.03 }); return 0.05; },
  tab: (o, t) => { click(o, t, 0.1, 1800); return 0.05; },
  coin: (o, t) => { coinPing(o, t); return 0.4; },
  coins: (o, t) => { for (let i = 0; i < 4; i++) coinPing(o, t + i * 0.06 + rand(0, 0.02), rand(1700, 2500), 0.12); return 0.6; },
  chaching: (o, t) => {
    noise(o, t, { type: 'bandpass', freq: 1600, q: 2, dur: 0.06, gain: 0.5, attack: 0.002 });
    noise(o, t + 0.07, { type: 'bandpass', freq: 900, q: 2, dur: 0.06, gain: 0.4, attack: 0.002 });
    bell(o, t + 0.13, 1568, 0.14, 1.1);
    bell(o, t + 0.13, 2093, 0.1, 1.1);
    for (let i = 0; i < 3; i++) coinPing(o, t + 0.2 + i * 0.07, rand(1900, 2600), 0.08);
    return 1.2;
  },
  buy: (o, t) => { coinPing(o, t, 1500, 0.14); click(o, t + 0.12, 0.2, 1200); return 0.4; },
  error: (o, t) => {
    tone(o, t, { freq: 150, to: 70, dur: 0.2, gain: 0.4 });
    tone(o, t, { type: 'square', freq: 92, dur: 0.14, gain: 0.04 });
    noise(o, t, { type: 'lowpass', freq: 350, dur: 0.1, gain: 0.25 });
    return 0.25;
  },
  chime: (o, t) => {
    [1047, 1319, 1568, 2093].forEach((f, i) => { tone(o, t + i * 0.08, { type: 'triangle', freq: f, dur: 0.5, gain: 0.14 }); tone(o, t + i * 0.08, { freq: f * 2, dur: 0.3, gain: 0.04 }); });
    return 0.9;
  },
  fanfare: (o, t) => {
    const notes: [number, number, number][] = [[67, 0, 0.12], [72, 0.12, 0.12], [76, 0.24, 0.12], [79, 0.36, 0.9]];
    for (const [n, at, d] of notes) brass(o, t + at, midi(n), d, 0.1);
    for (const n of [60, 64, 67]) brass(o, t + 0.36, midi(n), 0.9, 0.05);
    noise(o, t + 0.36, { type: 'highpass', freq: 6000, dur: 1.2, gain: 0.12, attack: 0.005 }); // cymbal
    tone(o, t + 0.36, { freq: 65, to: 40, dur: 0.5, gain: 0.5 }); // timpani
    return 1.4;
  },
  medal: (o, t) => { bell(o, t, 1760, 0.16); bell(o, t + 0.12, 2349, 0.12); tone(o, t + 0.2, { freq: 3520, dur: 0.6, gain: 0.03 }); return 1.5; },
  punch: (o, t) => { punch(o, t); return 0.2; },
  gunshot: (o, t) => { gunshot(o, t); return 0.8; },
  cheer: (o, t) => {
    for (let i = 0; i < 7; i++) noise(o, t + rand(0, 0.15), { type: 'bandpass', freq: rand(450, 1300), q: 1.6, dur: 1.5, gain: 0.09, attack: 0.25, curve: 'lin' });
    for (let i = 0; i < 4; i++) {
      const f = rand(180, 280);
      tone(o, t + rand(0, 0.1), { type: 'sawtooth', freq: f, to: f * 1.12, dur: 1.2, gain: 0.025, attack: 0.2, curve: 'lin', filter: { type: 'bandpass', freq: 600, q: 3 }, vibrato: { rate: rand(4, 6), depth: 4 } });
    }
    return 1.6;
  },
  groan: (o, t) => {
    tone(o, t, { type: 'sawtooth', freq: 155, to: 92, dur: 0.85, gain: 0.18, attack: 0.05, curve: 'lin', filter: { type: 'lowpass', freq: 700, q: 2 }, vibrato: { rate: 5, depth: 4 } });
    tone(o, t, { type: 'sawtooth', freq: 158, to: 95, dur: 0.85, gain: 0.08, attack: 0.05, curve: 'lin', filter: { type: 'bandpass', freq: 450, q: 4 } });
    return 0.9;
  },
  bossHit: (o, t) => { punch(o, t, 1.6); noise(o, t, { type: 'lowpass', freq: 3000, to: 300, dur: 0.25, gain: 0.4, attack: 0.001 }); return 0.4; },
  roar: (o, t) => {
    const a = ac!;
    const shaper = a.createWaveShaper();
    // Odd length so that silence maps exactly to 0 (an even curve leaves a hum after the roar).
    const curve = new Float32Array(257);
    for (let i = 0; i < 257; i++) { const x = (i / 128) - 1; curve[i] = Math.tanh(x * 3); }
    shaper.curve = curve;
    const trem = a.createGain();
    const lfo = a.createOscillator();
    const lfoDepth = a.createGain();
    lfo.frequency.value = 17;
    lfoDepth.gain.value = 0.35;
    trem.gain.value = 0.65;
    lfo.connect(lfoDepth).connect(trem.gain);
    lfo.start(t);
    lfo.stop(t + 1.5);
    shaper.connect(trem).connect(o);
    for (const f of [68, 71.5]) tone(shaper, t, { type: 'sawtooth', freq: f, to: f * 0.8, dur: 1.4, gain: 0.25, attack: 0.08, curve: 'lin', filter: { type: 'lowpass', freq: 300, to: 1200, q: 3 } });
    noise(shaper, t, { type: 'bandpass', freq: 600, to: 250, q: 1, dur: 1.4, gain: 0.3, attack: 0.08, curve: 'lin' });
    return 1.5;
  },
  deal: (o, t) => { noise(o, t, { type: 'bandpass', freq: 1500, to: 5000, q: 1, dur: 0.1, gain: 0.3, attack: 0.01 }); return 0.12; },
  flip: (o, t) => { click(o, t, 0.25, 3500); click(o, t + 0.045, 0.15, 2500); return 0.1; },
  chips: (o, t) => {
    const n = 5;
    for (let i = 0; i < n; i++) {
      const at = t + i * rand(0.03, 0.07);
      tone(o, at, { freq: rand(3200, 4600), dur: 0.05, gain: 0.08 });
      noise(o, at, { type: 'bandpass', freq: 5000, q: 2, dur: 0.025, gain: 0.15, attack: 0.001 });
    }
    return 0.4;
  },
  chipSlide: (o, t) => { noise(o, t, { type: 'lowpass', freq: 1200, to: 3000, dur: 0.45, gain: 0.15, attack: 0.05, curve: 'lin' }); RECIPES.chips(o, t + 0.4, {}); return 0.9; },
  spin: (o, t, { duration = 2 }) => {
    for (let x = 0; x < duration; x += 1 / 18) click(o, t + x, 0.12, 2400 + Math.random() * 400);
    tone(o, t, { type: 'sawtooth', freq: 55, dur: duration, gain: 0.05, attack: 0.1, curve: 'lin', filter: { type: 'lowpass', freq: 220 } });
    return duration;
  },
  reelStop: (o, t) => { tone(o, t, { freq: 230, to: 110, dur: 0.12, gain: 0.35, attack: 0.001 }); noise(o, t, { type: 'lowpass', freq: 900, dur: 0.06, gain: 0.3, attack: 0.001 }); return 0.15; },
  win: (o, t) => {
    [1047, 1319, 1568, 2093].forEach((f, i) => tone(o, t + i * 0.07, { type: 'square', freq: f, dur: 0.18, gain: 0.05, filter: { type: 'lowpass', freq: 4000 } }));
    tone(o, t + 0.3, { type: 'triangle', freq: 2093, dur: 0.6, gain: 0.12 });
    return 0.9;
  },
  bigWin: (o, t) => { RECIPES.win(o, t, {}); for (let i = 0; i < 10; i++) coinPing(o, t + 0.3 + i * 0.07, rand(1700, 2700), 0.08); return 1.2; },
  jackpot: (o, t) => {
    for (let x = 0; x < 2.4; x += 0.36) {
      tone(o, t + x, { type: 'square', freq: 988, dur: 0.17, gain: 0.06, attack: 0.01, curve: 'lin', filter: { type: 'lowpass', freq: 3000 } });
      tone(o, t + x + 0.18, { type: 'square', freq: 740, dur: 0.17, gain: 0.06, attack: 0.01, curve: 'lin', filter: { type: 'lowpass', freq: 3000 } });
    }
    for (let x = 0; x < 2.4; x += 0.3) bell(o, t + x, rand(1500, 2400), 0.07, 0.8);
    for (let i = 0; i < 24; i++) coinPing(o, t + 0.2 + i * 0.09, rand(1600, 2800), 0.06);
    return 2.6;
  },
  lose: (o, t) => {
    tone(o, t, { type: 'sawtooth', freq: 392, to: 370, dur: 0.28, gain: 0.1, attack: 0.02, filter: { type: 'lowpass', freq: 900 } });
    tone(o, t + 0.3, { type: 'sawtooth', freq: 349, to: 311, dur: 0.55, gain: 0.1, attack: 0.02, filter: { type: 'lowpass', freq: 800 }, vibrato: { rate: 6, depth: 6 } });
    return 0.9;
  },
  ball: (o, t, { duration = 4 }) => {
    // Clicks that space out as the ball slows, then a few bounces as it drops into a pocket.
    let x = 0;
    let gap = 0.045;
    while (x < duration - 0.5) {
      click(o, t + x, 0.13 * rand(0.6, 1), 3600);
      x += gap;
      gap = 0.045 + Math.pow(x / duration, 2.2) * 0.3;
    }
    noise(o, t, { type: 'lowpass', freq: 500, dur: duration - 0.4, gain: 0.05, attack: 0.1, curve: 'lin' });
    [0, 0.13, 0.22, 0.28].forEach((b, i) => click(o, t + duration - 0.45 + b, 0.3 - i * 0.05, 2200));
    return duration;
  },
  knock: (o, t) => { woodKnock(o, t); woodKnock(o, t + 0.15); return 0.3; },
  bugle: (o, t) => {
    const tune: [number, number][] = [[67, 0.14], [72, 0.14], [76, 0.14], [79, 0.28], [76, 0.14], [79, 0.14], [84, 0.6]];
    let at = 0;
    for (const [n, d] of tune) { brass(o, t + at, midi(n), d * 0.95, 0.09); at += d; }
    return at;
  },
  hooves: (o, t, { duration = 6 }) => {
    for (let x = 0; x < duration; x += 0.34) {
      const fade = Math.min(1, x / 0.6, (duration - x) / 0.8);
      for (const off of [0, 0.07, 0.14]) hoof(o, t + x + off + rand(0, 0.01), 0.22 * fade);
    }
    return duration;
  },
  drum: (o, t) => { drum(o, t); drum(o, t + 0.45); drum(o, t + 0.75, 0.6); drum(o, t + 0.9, 0.9); return 1.4; },
  horn: (o, t) => { for (const n of [48, 55, 60, 64]) brass(o, t, midi(n), 1.5, 0.08); return 1.6; },
  pop: (o, t) => { tone(o, t, { freq: 480, to: 980, dur: 0.08, gain: 0.25, attack: 0.004 }); return 0.1; },
  mention: (o, t) => { tone(o, t, { type: 'triangle', freq: 880, dur: 0.15, gain: 0.22 }); tone(o, t + 0.11, { type: 'triangle', freq: 1320, dur: 0.3, gain: 0.2 }); return 0.45; },
  alert: (o, t) => { tone(o, t, { type: 'triangle', freq: 784, dur: 0.14, gain: 0.2 }); tone(o, t + 0.15, { type: 'triangle', freq: 587, dur: 0.3, gain: 0.2 }); return 0.5; },
  like: (o, t) => { tone(o, t, { freq: 700, to: 1400, dur: 0.1, gain: 0.2, attack: 0.004 }); tone(o, t + 0.07, { freq: 1760, dur: 0.15, gain: 0.08 }); return 0.25; },
  heal: (o, t) => { tone(o, t, { freq: 400, to: 1200, dur: 0.5, gain: 0.12, attack: 0.05 }); [1319, 1760, 2637].forEach((f, i) => tone(o, t + 0.2 + i * 0.07, { freq: f, dur: 0.4, gain: 0.05 })); return 0.8; },
  munch: (o, t) => { for (let i = 0; i < 3; i++) noise(o, t + i * 0.16, { type: 'lowpass', freq: 1200, dur: 0.09, gain: 0.35, attack: 0.005 }); RECIPES.chime(o, t + 0.5, {}); return 1.3; },
  whoosh: (o, t) => { noise(o, t, { type: 'bandpass', freq: 400, to: 2500, q: 1.2, dur: 0.35, gain: 0.2, attack: 0.12, curve: 'lin' }); return 0.4; },
  swords: (o, t) => { gunshot(o, t); punch(o, t + 0.25); gunshot(o, t + 0.45); return 0.9; },
  // v0.7 additions for the new features
  stamp: (o, t) => {
    // a rubber stamp: a dull thump of wood on paper with a little slap
    tone(o, t, { freq: 140, to: 70, dur: 0.12, gain: 0.55, attack: 0.002 });
    noise(o, t, { type: 'lowpass', freq: 1200, to: 300, dur: 0.09, gain: 0.5, attack: 0.001 });
    noise(o, t + 0.01, { type: 'bandpass', freq: 2500, q: 1.5, dur: 0.04, gain: 0.25, attack: 0.001 });
    return 0.2;
  },
  envelope: (o, t) => {
    // paper sliding out of an envelope, then a flap
    noise(o, t, { type: 'bandpass', freq: 3000, to: 5000, q: 0.8, dur: 0.35, gain: 0.18, attack: 0.06, curve: 'lin' });
    noise(o, t + 0.38, { type: 'highpass', freq: 2500, dur: 0.06, gain: 0.25, attack: 0.002 });
    return 0.5;
  },
  bell: (o, t) => { bell(o, t, 2093, 0.14, 1.0); return 1.0; },
  typewriter: (o, t) => {
    for (let i = 0; i < 6; i++) click(o, t + i * rand(0.06, 0.11), 0.22, rand(1800, 2600));
    bell(o, t + 0.75, 2093, 0.1, 0.9);
    return 1.6;
  },
  siren: (o, t) => {
    for (let i = 0; i < 2; i++) tone(o, t + i * 0.9, { type: 'sawtooth', freq: 520, to: 880, dur: 0.85, gain: 0.06, attack: 0.1, curve: 'lin', filter: { type: 'lowpass', freq: 1800 } });
    drum(o, t, 0.5);
    return 1.8;
  },
  chest: (o, t) => {
    // a lid creaks open, then coins and a chime
    tone(o, t, { type: 'sawtooth', freq: 120, to: 200, dur: 0.35, gain: 0.05, attack: 0.05, curve: 'lin', filter: { type: 'bandpass', freq: 600, q: 6 } });
    noise(o, t + 0.3, { type: 'lowpass', freq: 800, dur: 0.1, gain: 0.35, attack: 0.002 });
    for (let i = 0; i < 8; i++) coinPing(o, t + 0.4 + i * 0.06, rand(1700, 2700), 0.1);
    [1319, 1568, 2093].forEach((f, i) => tone(o, t + 0.9 + i * 0.09, { type: 'triangle', freq: f, dur: 0.5, gain: 0.12 }));
    return 1.6;
  },
  // v0.8
  gavel: (o, t) => {
    // the auctioneer's hammer: two quick taps and a hard knock on the block
    const knockAt = (at: number, g: number) => {
      tone(o, at, { freq: 420, to: 180, dur: 0.07, gain: g, attack: 0.001 });
      noise(o, at, { type: 'bandpass', freq: 1400, q: 2, dur: 0.05, gain: g * 0.8, attack: 0.001 });
    };
    knockAt(t, 0.3); knockAt(t + 0.16, 0.3); knockAt(t + 0.42, 0.6);
    tone(o, t + 0.42, { freq: 110, to: 60, dur: 0.25, gain: 0.35, attack: 0.002 });
    return 0.8;
  },
  streak: (o, t) => {
    // a rising run on the horns, then the crowd
    [55, 59, 62, 67].forEach((n, i) => brass(o, t + i * 0.09, midi(n), 0.35, 0.06));
    brass(o, t + 0.4, midi(71), 0.9, 0.07);
    RECIPES.cheer(o, t + 0.3, { duration: 1.2 });
    return 1.6;
  },
  handshake: (o, t) => {
    // a handshake: a stamp on the contract and the till
    RECIPES.stamp(o, t, {});
    RECIPES.chaching(o, t + 0.2, {});
    return 1.0;
  },
  prestige: (o, t) => {
    // v0.9: a whoosh back to the start, a rising brass line, a full chord with bells and cymbal.
    noise(o, t, { type: 'bandpass', freq: 900, q: 0.8, dur: 0.7, gain: 0.12, attack: 0.5, curve: 'lin' });
    const line: [number, number][] = [[60, 0.55], [64, 0.7], [67, 0.85], [72, 1.0]];
    for (const [n, at] of line) brass(o, t + at, midi(n), 0.16, 0.09);
    for (const n of [60, 64, 67, 72, 76]) brass(o, t + 1.15, midi(n), 1.6, 0.05);
    tone(o, t + 1.15, { freq: 65, to: 40, dur: 0.7, gain: 0.5 });
    noise(o, t + 1.15, { type: 'highpass', freq: 6000, dur: 1.8, gain: 0.12, attack: 0.005 });
    [1568, 2093, 2637, 3136].forEach((f, i) => bell(o, t + 1.25 + i * 0.1, f, 0.08));
    return 3.0;
  },
};

export type SoundName = keyof typeof RECIPES;

/** Volume trims so every sound sits at a similar loudness (measured by rendering each one offline). */
const LEVEL: Record<string, number> = {
  click: 5, tab: 6, flip: 3, deal: 1.8, pop: 1.6, like: 1.6, whoosh: 1.6, chips: 1.4, ball: 1.4, spin: 1.2,
  drum: 0.5, swords: 0.45, roar: 0.55, knock: 0.6, fanfare: 0.8, horn: 0.8, cheer: 1.6, stamp: 0.9, gavel: 1.2, streak: 0.7, handshake: 0.9, envelope: 1.6, typewriter: 1.2, siren: 0.9, prestige: 0.8,
};

const lastPlayed = new Map<string, number>();
/** The last sounds played, for the automated browser test (window.__mmSound). */
const recent: string[] = [];
(window as any).__mmSound = { recent, state: () => ac?.state ?? 'none' };

export interface SoundHandle { stop: () => void }
const NOOP: SoundHandle = { stop: () => {} };

/** Play one of the sounds above. Silent if sound is off or the player hasn't clicked yet. */
export function play(name: SoundName | string, opts: PlayOpts = {}): SoundHandle {
  const s = getSettings();
  if (!ac || !unlocked || !s.sfx || s.muted || s.master <= 0 || document.hidden) return NOOP;
  const recipe = RECIPES[name];
  if (!recipe) return NOOP;
  const now = performance.now();
  if (now - (lastPlayed.get(name) ?? 0) < 40) return NOOP; // don't stack the same sound in one instant
  lastPlayed.set(name, now);
  const out = ac.createGain();
  out.gain.value = (opts.volume ?? 1) * (LEVEL[name] ?? 1);
  out.connect(sfxBus);
  const t = ac.currentTime + 0.01;
  const len = recipe(out, t, opts);
  recent.push(name);
  if (recent.length > 300) recent.shift();
  const cleanup = setTimeout(() => out.disconnect(), (len + 1) * 1000);
  return {
    stop: () => {
      if (!ac) return;
      out.gain.setTargetAtTime(0, ac.currentTime, 0.03);
      clearTimeout(cleanup);
      setTimeout(() => out.disconnect(), 300);
    },
  };
}

/** Play a list of [delay ms, sound] steps. */
export function sequence(steps: [number, SoundName | string][]) {
  for (const [ms, name] of steps) setTimeout(() => play(name), ms);
}

export const SOUND_NAMES = Object.keys(RECIPES);
